import 'configuration/ag-grid/ag-grid.init';
import { makeValuesPredicate } from '@sgme/fp';
import type { AgGridEvent, ColDef, ColTypeDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { Asset, CsvResponse } from 'apiConnector/underlying-universe';
import { BlotterErrorIcon } from 'components/Blotters/BlotterErrorIcon';
import { BlotterSpinner } from 'components/Blotters/BlotterSpinner';
import { FullTextSearch } from 'components/Blotters/Filter/full-text-search';
import { EsgEligibilityIcon } from 'components/esg-eligibility/esg-eligibility-icon';
import { getEsgEligibilityTranslateMessageByEligibility } from 'components/esg-eligibility/utils';
import { GridWrapperWithTheme } from 'components/shared/Blotter/Grid';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { Pagination } from 'components/shared/Blotter/Pagination/Pagination';
import { useAgGridIntl } from 'hooks/ag-grid/useAgGridIntl';
import type { IntlKey } from 'locales';
import type { Underlying } from 'models/underlying';
import { EquityUnderlying } from 'models/underlying/equity';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Observable } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import styled from 'styled-components';
import { useDebounceValue } from 'usehooks-ts';

import { UnderlyingUniverseSearchContext } from './UnderlyingUniverseGrid.context';
import { fullTextSearch } from './UnderlyingUniverseGrid.helper';

const StyledAgGridReact = styled(AgGridReact<Underlying>)`
  flex-grow: 1;
`;

const StyledPagination = styled(Pagination)`
  flex-grow: 0;
`;

interface Props {
  underlyings?: Underlying[];
  columnDefs: ColDef<Underlying>[];
}

interface ExportCsvProps {
  asset: Asset;
  fetchCSV(asset: Asset, query?: string): Observable<CsvResponse>;
}

const isKnownHeader = makeValuesPredicate([
  'transactionType',
  'seniorityType',
  'type',
  'name',
  'bloombergTickerCode',
  'reutersCode',
  'isin',
  'currency',
  'exchange',
  'website',
  'id',
  'marketPlace',
  'masterDesk',
  'currency1',
  'currency2',
  'defaultCutOff',
  'cutOffs',
  'esgEligibility',
]);

function getHeaderNameTranslationKey(headerName: string): IntlKey {
  if (isKnownHeader(headerName)) {
    return `underlyingUniverse.grid.column.${headerName}`;
  } else {
    return headerName as any;
  }
}

export function UnderlyingUniverseSearch() {
  const { searchFilter, setSearchFilter } = useContext(UnderlyingUniverseSearchContext);
  return (
    <FullTextSearch value={searchFilter} onChange={setSearchFilter} placeholder="blotter.filter.label.quickSearch" />
  );
}
export function ExportCsvButton({ asset }: ExportCsvProps) {
  const { formatMessage } = useIntl();
  const [isExporting] = useState<boolean>(false);
  const [hasExportError] = useState<boolean>(false);
  const { exportCsv } = useContext(UnderlyingUniverseSearchContext);

  const onExportCSV = useCallback(() => {
    const fileName = `${asset}Underlyings.csv`;
    exportCsv?.(fileName);
    logAnalytics.trackEvent('Underlying universe', 'Export CSV');
  }, [exportCsv, asset]);

  return (
    <div className="form-group ms-auto">
      <div data-e2e="export-csv">
        {!isExporting && (
          <button
            className="btn btn-flat-secondary btn-xl btn-icon border-start border-opacity-40"
            title={formatMessage({ id: 'blotter.filter.label.exportCSV' })}
            data-e2e="export-csv-btn"
            disabled={!exportCsv}
            onClick={onExportCSV}
          >
            <i className="icon">save_alt</i>
          </button>
        )}
        {isExporting && <BlotterSpinner />}
        {hasExportError && <BlotterErrorIcon message={formatMessage({ id: 'downloadCsv.Error' })} />}
      </div>
    </div>
  );
}

export function UnderlyingUniverseGrid({ underlyings = [], columnDefs }: Props) {
  const getLocaleText = useAgGridIntl();
  const { formatMessage } = useIntl();

  const { searchFilter, setExportCsv } = useContext(UnderlyingUniverseSearchContext);
  const [debouncedSearchFilter] = useDebounceValue(searchFilter, 500);

  const customColumns = useMemo(() => {
    return {
      esgEligibility: {
        filter: true,
        cellRenderer: ({ data }: ICellRendererParams<EquityUnderlying>) => (
          <EsgEligibilityIcon
            isEligible={data?.esgEligibility}
            isEligibleWithCondition={data?.esgExtendedEligibility}
          />
        ),
        valueGetter: ({ data }: ValueGetterParams<EquityUnderlying>) =>
          getEsgEligibilityTranslateMessageByEligibility(
            formatMessage,
            data?.esgEligibility,
            data?.esgExtendedEligibility,
          ),
      } as ColTypeDef<Underlying, string>,
    };
  }, [formatMessage]);

  const filteredUnderlyings = useMemo(() => {
    return fullTextSearch(underlyings, debouncedSearchFilter);
  }, [underlyings, debouncedSearchFilter]);

  const totalItems = useMemo<number>(() => filteredUnderlyings.length, [filteredUnderlyings]);

  const defaultColumnDefinition = useDefaultColumnDefinition<Underlying>(getHeaderNameTranslationKey);
  const columnTypes = useClientSideDefaultColumnTypes<Underlying>(customColumns);

  const onAutosizeGrid = useCallback(({ api }: AgGridEvent<Underlying>) => {
    api.sizeColumnsToFit();
  }, []);

  const onGridReady = useCallback(
    (event: AgGridEvent<Underlying>) => {
      const exportFunc = (fileName: string) => {
        event.api.exportDataAsCsv({
          fileName,
        });
      };

      setExportCsv(() => exportFunc);
    },
    [setExportCsv],
  );

  return (
    <>
      <GridWrapperWithTheme className="d-flex flex-column flex-grow-1" condensed={false}>
        <StyledAgGridReact
          rowData={filteredUnderlyings}
          suppressMultiSort
          columnDefs={columnDefs}
          defaultColDef={defaultColumnDefinition}
          columnTypes={columnTypes}
          getLocaleText={getLocaleText}
          onFirstDataRendered={onAutosizeGrid}
          onGridReady={onGridReady}
          onRowDataUpdated={onAutosizeGrid}
          cellSelection
        />
      </GridWrapperWithTheme>

      <StyledPagination totalItems={totalItems} showOnlySummary={true} />
    </>
  );
}

UnderlyingUniverseGrid.displayName = 'UnderlyingUniverseGrid';
